import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { AlertCircle, ChevronDown, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import ConfirmationModal from './components/ConfirmationModal';
import {
  createSlackAgentInstallUrl,
  getMyOrg,
  listSlackAgentInstallations,
  revokeSlackAgentInstallation,
} from './lib/api';

export function SlackMark({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="6.3" y="0" width="5.05" height="11.35" rx="2.52" fill="#36C5F0" />
      <circle cx="2.52" cy="8.83" r="2.52" fill="#36C5F0" />
      <rect x="12.65" y="6.3" width="11.35" height="5.05" rx="2.52" fill="#2EB67D" />
      <circle cx="15.17" cy="2.52" r="2.52" fill="#2EB67D" />
      <rect x="12.65" y="12.65" width="5.05" height="11.35" rx="2.52" fill="#ECB22E" />
      <circle cx="21.48" cy="15.17" r="2.52" fill="#ECB22E" />
      <rect x="0" y="12.65" width="11.35" height="5.05" rx="2.52" fill="#E01E5A" />
      <circle cx="8.83" cy="21.48" r="2.52" fill="#E01E5A" />
    </svg>
  );
}

export const SLACK_INSTALLATION_PRESENTATION = {
  active: { label: 'Connected', tone: 'connected' },
  reinstall_required: { label: 'Reinstall required', tone: 'warning' },
  error: { label: 'Needs attention', tone: 'warning' },
  revoked: { label: 'Disconnected', tone: 'disconnected' },
};

export function slackInstallationPresentation(status) {
  return SLACK_INSTALLATION_PRESENTATION[status] || {
    label: status || 'Unknown',
    tone: 'disconnected',
  };
}

function SlackInstallationRow({ installation, canManage, onReconnect, onDisconnect, busy }) {
  const presentation = slackInstallationPresentation(installation.status);
  const canReconnect = ['reinstall_required', 'error'].includes(installation.status);
  const canDisconnect = installation.status !== 'revoked';

  return (
    <div className="slack-agent-installation-row">
      <div className="slack-agent-installation-main">
        <span className="slack-agent-workspace-avatar">
          {(installation.teamName || 'S').charAt(0).toUpperCase()}
        </span>
        <div>
          <strong>{installation.teamName || 'Slack workspace'}</strong>
          <span className={`integrations-account-status integrations-account-status--${presentation.tone}`}>
            {presentation.label}
          </span>
        </div>
      </div>
      <div className="slack-agent-installation-actions">
        {canManage && canReconnect && (
          <button type="button" onClick={onReconnect} disabled={busy}>
            {busy ? <Loader2 size={13} className="integrations-spin" /> : <RefreshCw size={13} />}
            Reinstall
          </button>
        )}
        {canManage && canDisconnect && (
          <button
            type="button"
            className="is-danger slack-agent-disconnect"
            onClick={onDisconnect}
            disabled={busy}
            aria-label={`Disconnect ${installation.teamName || 'Slack workspace'}`}
            title="Disconnect workspace"
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function SlackAgentIntegrationSection({
  navigateTo = (url) => window.location.assign(url),
  onConnectionChange,
}) {
  const [installations, setInstallations] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [installationToRevoke, setInstallationToRevoke] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expanded, setExpanded] = useState(false);
  const installationListId = useId();

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [orgResponse, installationResponse] = await Promise.all([
        getMyOrg(),
        listSlackAgentInstallations(),
      ]);
      setCanManage(orgResponse?.organization?.role === 'owner');
      setInstallations(installationResponse?.installations || []);
      setAvailable(true);
      setError('');
    } catch (err) {
      if (err.status === 404) {
        setAvailable(false);
      } else {
        setError('Could not load Slack Agent installations.');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('slack_agent') !== 'installed') return;
    setSuccess('Slack workspace connected. Team members can now link their Latted accounts.');
    url.searchParams.delete('slack_agent');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const activeInstallationCount = useMemo(
    () => installations.filter((installation) => installation.status === 'active').length,
    [installations],
  );

  useEffect(() => {
    onConnectionChange?.(installations.some((installation) => installation.status === 'active'));
  }, [installations, onConnectionChange]);

  const startInstall = useCallback(async () => {
    setConnecting(true);
    setError('');
    try {
      const result = await createSlackAgentInstallUrl('/settings/integrations');
      const installUrl = new URL(result.installUrl);
      if (installUrl.protocol !== 'https:' || installUrl.hostname !== 'slack.com') {
        throw new Error('The Slack installation URL could not be validated.');
      }
      navigateTo(installUrl.toString());
    } catch (err) {
      if (err.status === 403) {
        setError('Slack Agent is not enabled for this organization, or only an organization owner can install it.');
      } else {
        setError('Could not start Slack installation.');
      }
      setConnecting(false);
    }
  }, [navigateTo]);

  const revoke = useCallback(async () => {
    const installation = installationToRevoke;
    if (!installation) return;
    setInstallationToRevoke(null);
    setError('');
    try {
      await revokeSlackAgentInstallation(installation.id);
      setSuccess(`${installation.teamName || 'Slack workspace'} disconnected.`);
      await load({ silent: true });
    } catch (err) {
      setError('Could not disconnect this Slack workspace.');
    }
  }, [installationToRevoke, load]);

  if (!available) return null;

  return (
    <section className="slack-agent-section" aria-labelledby="slack-agent-heading">
      <div className="integrations-section-heading integrations-section-heading--first">
        <div>
          <h2 id="slack-agent-heading">Collaboration</h2>
          <p>Start a Latted project directly from your team workspace.</p>
        </div>
      </div>

      {error && <div className="integrations-error"><AlertCircle size={15} />{error}</div>}
      {success && <div className="integrations-pending">{success}</div>}

      <div className="integrations-grid integrations-grid--featured">
        <div className="integrations-card slack-agent-card">
          <div className="integrations-card-header">
            <button
              type="button"
              className="integrations-card-summary"
              onClick={() => setExpanded((current) => !current)}
              disabled={!installations.length}
              aria-expanded={installations.length ? expanded : undefined}
              aria-controls={installations.length ? installationListId : undefined}
              aria-label={installations.length ? `${expanded ? 'Hide' : 'Show'} Slack workspace connections` : undefined}
            >
              <span className="integrations-card-logo integrations-card-logo--slack">
                <SlackMark />
                {activeInstallationCount > 0 && (
                  <span className="integrations-card-logo-status" aria-label={`${activeInstallationCount} connected Slack workspace${activeInstallationCount === 1 ? '' : 's'}`}>
                    {activeInstallationCount}
                  </span>
                )}
              </span>
              <span className="integrations-card-heading">
                <span className="integrations-card-title">Latted Agent for Slack</span>
                <span className="integrations-card-desc">
                  Install the dedicated app, then mention <code>@Latted</code> with your project prompt.
                </span>
              </span>
              {installations.length > 0 && <ChevronDown size={16} className={`integrations-card-chevron${expanded ? ' is-expanded' : ''}`} />}
            </button>
            {!installations.length && (
              <button
                type="button"
                className="integrations-card-connect"
                onClick={startInstall}
                disabled={loading || connecting || !canManage}
                aria-label="Install Slack app"
                title={!canManage ? 'Only an organization owner can install Slack.' : 'Install Slack app'}
              >
                {connecting ? <Loader2 size={14} className="integrations-spin" /> : <Plus size={14} />}
              </button>
            )}
          </div>

          {loading ? (
            <div className="slack-agent-loading"><Loader2 size={15} className="integrations-spin" /> Loading Slack workspaces…</div>
          ) : installations.length && expanded ? (
            <div className="integrations-card-dropdown integrations-social-dropdown slack-agent-dropdown" id={installationListId}>
              <div className="integrations-social-actions">
                <span>Connected workspaces</span>
                <button
                  type="button"
                  onClick={startInstall}
                  disabled={connecting || !canManage}
                  aria-label="Add workspace"
                  title={!canManage ? 'Only an organization owner can add Slack workspaces.' : undefined}
                >
                  {connecting ? <Loader2 size={13} className="integrations-spin" /> : <Plus size={13} />}
                  Add workspace
                </button>
              </div>
              <div className="slack-agent-installation-list">
                {installations.map((installation) => (
                  <SlackInstallationRow
                    key={installation.id}
                    installation={installation}
                    canManage={canManage}
                    busy={connecting}
                    onReconnect={startInstall}
                    onDisconnect={() => setInstallationToRevoke(installation)}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <ConfirmationModal
        isOpen={Boolean(installationToRevoke)}
        title="Disconnect Slack workspace?"
        message={installationToRevoke ? `${installationToRevoke.teamName || 'This workspace'} will stop accepting Latted project commands. Existing projects are not affected.` : ''}
        confirmLabel="Disconnect"
        destructive
        onConfirm={revoke}
        onCancel={() => setInstallationToRevoke(null)}
      />
    </section>
  );
}
